import moment from 'moment';
import { mixed, object, string } from 'yup';

import {
  EDUCATION_DATE_FIELD_NAMES,
  EDUCATION_DESCRIPTION_MIN_LENGTH,
  EDUCATION_FIELD_NAMES,
  EDUCATION_HOURS_PER_WEEK_BOUNDS,
  EDUCATION_SCHOOL_NAME_MIN_LENGTH,
  EDUCATION_SETTING_HOMESCHOOL,
  EMPTY_EDUCATION_VALUES,
} from './constants';
import { TEXT_AREA_MAX_LENGTH } from '../constants';
import { API_DATE_FORMAT } from '../../../constants';
import { buildRichTextSchema } from '../utils';

import type { Moment } from 'moment';
import type {
  EducationFieldName,
  EducationFieldValue,
  EducationFormSchemaOptions,
  EducationFormValues,
  EducationInfo,
  EducationPayload,
  EducationSavedFieldValues,
} from './types';

function isDateFieldName(name: EducationFieldName): boolean {
  return EDUCATION_DATE_FIELD_NAMES.some((fieldName) => fieldName === name);
}

export function toEducationPayload(values: EducationFormValues): EducationPayload {
  return {
    education_setting: values.education_setting || null,
    number_of_hours_per_week:
      values.number_of_hours_per_week === '' ? null : Number(values.number_of_hours_per_week),
    school_name: values.school_name || null,
    school_phone_number: values.school_phone_number || null,
    education_description: values.education_description || null,
    focus_area_description: values.focus_area_description || null,
    client_education_evaluation_status: values.client_education_evaluation_status || null,
    client_education_evaluation_date: moment.isMoment(values.client_education_evaluation_date)
      ? values.client_education_evaluation_date.format(API_DATE_FORMAT)
      : null,
    iep_or_504_in_place: values.iep_or_504_in_place || null,
    education_support: values.education_support || null,
    client_classroom_setting: values.client_classroom_setting || null,
    client_attitude_towards_school: values.client_attitude_towards_school || null,
  };
}

export function toEducationFieldPayload<K extends EducationFieldName>(
  name: K,
  values: EducationFormValues,
): EducationPayload {
  const data: EducationPayload = {};
  data[name] = toEducationPayload(values)[name];
  return data;
}

export function mapEducationInfoToValues(
  educationInfo: EducationInfo | null | undefined,
): EducationFormValues {
  if (educationInfo == null) {
    return { ...EMPTY_EDUCATION_VALUES };
  }

  return {
    education_setting: educationInfo.education_setting ?? '',
    number_of_hours_per_week:
      educationInfo.number_of_hours_per_week != null
        ? String(educationInfo.number_of_hours_per_week)
        : '',
    school_name: educationInfo.school_name ?? '',
    school_phone_number: educationInfo.school_phone_number ?? '',
    education_description: educationInfo.education_description ?? '',
    focus_area_description: educationInfo.focus_area_description ?? '',
    client_education_evaluation_status: educationInfo.client_education_evaluation_status ?? '',
    client_education_evaluation_date: educationInfo.client_education_evaluation_date
      ? moment(educationInfo.client_education_evaluation_date)
      : null,
    iep_or_504_in_place: educationInfo.iep_or_504_in_place ?? '',
    education_support: educationInfo.education_support ?? '',
    client_classroom_setting: educationInfo.client_classroom_setting ?? '',
    client_attitude_towards_school: educationInfo.client_attitude_towards_school ?? '',
  };
}

export function getSavedEducationFieldValue(
  name: EducationFieldName,
  values: EducationFormValues,
): string {
  if (isDateFieldName(name)) {
    const dateValue = values[name];
    return moment.isMoment(dateValue) ? dateValue.format(API_DATE_FORMAT) : '';
  }
  const fieldValue = values[name];
  return fieldValue == null ? '' : String(fieldValue);
}

export function getSavedEducationFieldValues(
  values: EducationFormValues,
): EducationSavedFieldValues {
  return EDUCATION_FIELD_NAMES.reduce<EducationSavedFieldValues>(
    (saved, name) => ({ ...saved, [name]: getSavedEducationFieldValue(name, values) }),
    {},
  );
}

export function getChangedEducationFieldValues(
  values: EducationFormValues,
  savedValues: EducationSavedFieldValues,
): EducationSavedFieldValues {
  return EDUCATION_FIELD_NAMES.reduce<EducationSavedFieldValues>((changed, name) => {
    const savedValue = getSavedEducationFieldValue(name, values);
    if (savedValues[name] === savedValue) {
      return changed;
    }
    return { ...changed, [name]: savedValue };
  }, {});
}

export function getChangedEducationFieldNames(
  changedSavedValues: EducationSavedFieldValues,
): EducationFieldName[] {
  return EDUCATION_FIELD_NAMES.filter((name) => name in changedSavedValues);
}

export function getMergedEducationPatchData(
  values: EducationFormValues,
  names: EducationFieldName[],
): EducationPayload {
  return names.reduce<EducationPayload>(
    (data, name) => ({ ...data, ...toEducationFieldPayload(name, values) }),
    {},
  );
}

export function getEducationValuesWithFieldOverride(
  values: EducationFormValues,
  name: EducationFieldName,
  value: EducationFieldValue | undefined,
): EducationFormValues {
  if (value === undefined) {
    return values;
  }
  return { ...values, [name]: value };
}

export function isEducationDateFieldValid(
  name: EducationFieldName,
  values: EducationFormValues,
): boolean {
  if (!isDateFieldName(name)) {
    return true;
  }
  const dateValue = values[name];
  return dateValue == null || (moment.isMoment(dateValue) && dateValue.isValid());
}

function buildEducationDescriptionSchema(
  label: string,
  minLength: number,
  maxLength: number,
  richText: boolean,
) {
  const minMessage = `${label} must be at least ${minLength} characters`;
  const maxMessage = `${label} must be at most ${maxLength} characters`;

  if (!richText) {
    return string().trim().min(minLength, minMessage).max(maxLength, maxMessage).required();
  }

  return buildRichTextSchema({ minLength, maxLength, minMessage, maxMessage });
}

export function buildEducationFormSchema(options: EducationFormSchemaOptions = {}) {
  const schoolNameMinLength = options.schoolNameMinLength ?? EDUCATION_SCHOOL_NAME_MIN_LENGTH;
  const descriptionMinLength = options.descriptionMinLength ?? EDUCATION_DESCRIPTION_MIN_LENGTH;
  const descriptionMaxLength = options.descriptionMaxLength ?? TEXT_AREA_MAX_LENGTH;
  const richTextDescriptions = options.richTextDescriptions ?? false;

  return object({
    education_setting: string().required(),
    number_of_hours_per_week: string()
      .trim()
      .matches(/^\d+(\.\d)?$/, 'Enter a number with at most one decimal place')
      .test(
        'within-range',
        `Value must be greater than ${EDUCATION_HOURS_PER_WEEK_BOUNDS.min} and up to ${EDUCATION_HOURS_PER_WEEK_BOUNDS.max}`,
        (value) => {
          const parsed = Number(value);
          return (
            Number.isNaN(parsed) ||
            (parsed > EDUCATION_HOURS_PER_WEEK_BOUNDS.min &&
              parsed <= EDUCATION_HOURS_PER_WEEK_BOUNDS.max)
          );
        },
      )
      .required(),
    school_name: string()
      .trim()
      .test(
        'min-if-present',
        `School name must be at least ${schoolNameMinLength} characters`,
        (value) => !value || value.length >= schoolNameMinLength,
      )
      .when('education_setting', {
        is: (value: string) => value !== EDUCATION_SETTING_HOMESCHOOL,
        then: (schema) => schema.required(),
      }),
    school_phone_number: string()
      .trim()
      .matches(/^\d{10}$/, { message: 'Enter a 10-digit phone number', excludeEmptyString: true })
      .when('education_setting', {
        is: (value: string) => value !== EDUCATION_SETTING_HOMESCHOOL,
        then: (schema) => schema.required(),
      }),
    education_description: buildEducationDescriptionSchema(
      'Education description',
      descriptionMinLength,
      descriptionMaxLength,
      richTextDescriptions,
    ),
    focus_area_description: buildEducationDescriptionSchema(
      'Focus area description',
      descriptionMinLength,
      descriptionMaxLength,
      richTextDescriptions,
    ),
    client_education_evaluation_status: string().defined(),
    client_education_evaluation_date: mixed<Moment>().nullable().defined(),
    iep_or_504_in_place: string().required(),
    education_support: string()
      .trim()
      .min(
        descriptionMinLength,
        `Educational support must be at least ${descriptionMinLength} characters`,
      )
      .required(),
    client_classroom_setting: string().required(),
    client_attitude_towards_school: string().required(),
  });
}
